/**
 * MktAlertModal — small centered dialog using marketplace modal styles.
 * Used for blocked actions (delete project, insufficient credits, etc.).
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';

import '../../../pages/MarketplacePage/MarketplaceDetailModal.css';
// Trigger-modal-look overrides, scoped to the `.mkt-alert-modal` marker below.
// Imported AFTER the base sheet so the scoped rules win.
import './MktAlertModal.css';

/**
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {string} title
 * @param {React.ReactNode} children
 * @param {string} [ariaLabel]
 * @param {number} [maxWidth=420]
 * @param {string} [primaryLabel] — CTA button (navigates via primaryTo or onPrimary)
 * @param {string} [primaryTo] — react-router path for primary CTA
 * @param {() => void} [onPrimary] — runs before navigate/close when primary clicked
 * @param {string} [secondaryLabel='Got it']
 * @param {boolean} [stack=true] — raise z-index above another mkt-modal
 */
export default function MktAlertModal({
  open,
  onClose,
  title,
  children,
  ariaLabel,
  maxWidth = 420,
  primaryLabel,
  primaryLoading = false,
  primaryTo,
  onPrimary,
  secondaryLabel = 'Got it',
  stack = true,
  className = '',
}) {
  const navigate = useNavigate();

  if (!open) return null;

  const handlePrimary = () => {
    onPrimary?.();
    if (primaryTo) navigate(primaryTo);
    onClose?.();
  };

  return createPortal(
    <div
      className={`mkt-modal-overlay${stack ? ' mkt-modal-overlay--stack' : ''}`}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`mkt-modal mkt-alert-modal${className ? ` ${className}` : ''}`}
        style={{ maxWidth }}
        role="dialog"
        aria-label={ariaLabel || title}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="mkt-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <div className="mkt-modal-body">
          <header
            className="mkt-modal-header"
            style={{
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              paddingBottom: 18,
              marginBottom: 18,
            }}
          >
            <h2 className="mkt-modal-title">{title}</h2>
          </header>
          {children}
          <div className="mkt-alert-modal-actions">
            {primaryLabel && (
              <button
                type="button"
                className={`mkt-modal-deploy mkt-modal-deploy--full${primaryLoading ? ' is-loading' : ''}`}
                onClick={handlePrimary}
                disabled={primaryLoading}
                aria-busy={primaryLoading}
              >
                {primaryLabel}
              </button>
            )}
            {secondaryLabel && (
              <button
                type="button"
                className={`mkt-alert-modal-secondary${primaryLabel ? '' : ' mkt-modal-deploy mkt-modal-deploy--full'}`}
                onClick={onClose}
              >
                {secondaryLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Inline link styled for dark modals — pairs with MktAlertModal body copy. */
export function MktAlertModalLink({ to, children, onClick }) {
  if (onClick) {
    return (
      <button type="button" className="mkt-alert-modal-link" onClick={onClick}>
        {children}
      </button>
    );
  }
  return (
    <Link to={to} className="mkt-alert-modal-link" onClick={onClick}>
      {children}
    </Link>
  );
}
